import { useAuth } from '../../contexts/AuthContext';
import { V2_BASE, v2Path } from '../nav';
import { useNewUiAccess } from '../useNewUiAccess';
import { PRIMARY_CTA, SECONDARY_CTA } from './landingConfig';

export interface LandingCta {
  label: string;
  to: string;
}

/**
 * CTAs for the landing page, resolved against who is actually looking at it.
 *
 * The page was written for a logged-out marketing visitor, so every CTA pointed
 * at `/login`. While the revamp was allowlist-gated the only people who could
 * reach `/v2` were already signed in, so those CTAs sent them to a login screen
 * they didn't need — hence the signed-in branch below.
 *
 * `useNewUiAccess()` alone is NOT that test any more. Since the switchover
 * `isNewUiUser()` returns true unconditionally (see `newui/access.ts`), so
 * asking it who is signed in answered "everyone" — and a logged-out visitor on
 * the public front door was shown `Open FlashTools`, sent to `/v2`, and bounced
 * to a login form, having never been offered a trial or shown a price. Sign-in
 * state has to come from the session itself; access only decides *where* a
 * signed-in visitor is sent.
 *
 * Signed out — the visitor this page exists for — always gets the marketing
 * CTA, the trial microcopy, and `/login`, which renders both sign-in and
 * sign-up.
 */
export function useLandingCtas(): {
  primary: LandingCta;
  secondary: LandingCta;
  signedIn: boolean;
} {
  const { user, isAdminSession } = useAuth();
  const { allowed } = useNewUiAccess();
  const signedIn = Boolean(user || isAdminSession);

  if (signedIn && allowed) {
    return {
      primary: { label: 'Open FlashTools', to: V2_BASE },
      // v1 is no longer mounted — every legacy path just redirects back into
      // /v2 — so "Back to current app" pointed at a round trip. A signed-in
      // visitor's second action here is their account, not a second door into
      // the same app.
      secondary: { label: 'Billing', to: v2Path('/billing') },
      signedIn: true,
    };
  }

  return { primary: PRIMARY_CTA, secondary: SECONDARY_CTA, signedIn: false };
}
